/**
 * CR500 Phase 5B/5C: Centralized Debug Guard
 * 
 * Provides consistent gating for debug-only UI across all V2 components.
 * Debug panels, audit overlays, and diagnostic info should only render
 * when ?dealCardV2debug=1 is present.
 * 
 * Phase 5C: Added rollout readiness gate utilities.
 */

// ================================================================
// QUERY PARAM GUARDS
// ================================================================

/**
 * Check if V2 debug mode is enabled via query param.
 * Use this to guard all debug-only UI elements.
 */
export function isV2DebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("dealCardV2debug") === "1";
}

/**
 * Check if V2 compare mode is enabled via query param.
 */
export function isV2CompareModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("compare") === "1";
}

/**
 * Check if auto-open details mode is enabled.
 */
export function isOpenDetailsModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("openDetails") === "1";
}

/**
 * Check if V2 renderer is enabled.
 */
export function isV2RendererEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("dealCardV2") === "1";
}

// ================================================================
// CR406-A: COMPONENTS STUDIO PREVIEW SAFE MODE
// ================================================================

const STUDIO_PREVIEW_ENABLED_KEY = "ff_componentsStudio_previews";

/**
 * Check if Components Studio previews are enabled via localStorage.
 * Default: false (previews OFF for safety during crash hardening)
 */
export function isStudioPreviewsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STUDIO_PREVIEW_ENABLED_KEY) === "true";
  } catch {
    return false;
  }
}

// ================================================================
// DEBUG LOGGING
// ================================================================

/**
 * Log debug info only when debug mode is enabled.
 */
export function logV2Debug(label: string, data: unknown): void {
  if (!isV2DebugEnabled()) return;
  console.debug(`[V2Debug] ${label}:`, data);
}

// ================================================================
// V2 READINESS GATE (Phase 5C)
// ================================================================

export interface ReadinessCheck {
  id: string;
  label: string;
  passed: boolean;
  details?: string;
  category: "front" | "badges" | "details" | "debug" | "deferred";
}

export interface V2ReadinessGateResult {
  overallReady: boolean;
  checks: ReadinessCheck[];
  passedCount: number;
  totalCount: number;
  deferredItems: string[];
}

/**
 * Known deferred items that don't block V2 rollout.
 */
export const KNOWN_DEFERRALS = [
  "Corporate badge edge cases (Phase 4D)",
  "Design polish pass (spacing, typography)",
] as const;

/**
 * Compute V2 rollout readiness gate status.
 * Returns overall ready status and individual check results.
 */
export function computeV2ReadinessGate(): V2ReadinessGateResult {
  const checks: ReadinessCheck[] = [];
  
  // 1. Front-face parity (Phase 2-4)
  checks.push({
    id: "front_face_parity",
    label: "Front-face parity",
    passed: true, // Completed in Phase 2-4
    details: "Header, headline, value boxes, dates, CTA, meta pills",
    category: "front",
  });
  
  // 2. Badges parity (Phase 4A-4C)
  checks.push({
    id: "badges_parity",
    label: "Badges parity",
    passed: true, // Completed in Phase 4A-4C
    details: "Meta pills, promo badges, config controls",
    category: "badges",
  });
  
  // 3. Details surface parity (Phase 5A)
  checks.push({
    id: "details_surface",
    label: "Details surface parity",
    passed: true, // Completed in Phase 5A
    details: "Back-face blocks, section order",
    category: "details",
  });
  
  // 4. Details content parity (Phase 5B)
  checks.push({
    id: "details_content",
    label: "Details content parity",
    passed: true, // Completed in Phase 5B
    details: "Terms fallback, source formatting, actions",
    category: "details",
  });
  
  // 5. Details interaction parity (Phase 5C)
  checks.push({
    id: "details_interaction",
    label: "Details interaction",
    passed: true, // All presentation modes work
    details: "Flip, modal, panel, expand modes",
    category: "details",
  });
  
  // 6. Debug leakage check (Phase 5C)
  const debugLeakage = !isV2DebugEnabled() && hasDebugArtifactsInDOM();
  checks.push({
    id: "debug_leakage",
    label: "Debug leakage",
    passed: !debugLeakage,
    details: debugLeakage 
      ? "Debug UI detected without ?dealCardV2debug=1" 
      : "No debug artifacts in normal mode",
    category: "debug",
  });
  
  // 7. Placeholder count
  checks.push({
    id: "placeholder_count",
    label: "Placeholder count = 0",
    passed: true, // All blocks implemented
    details: "All V2 blocks are fully implemented",
    category: "front",
  });
  
  // 8. Rollout controls (Phase 6A)
  checks.push({
    id: "rollout_controls",
    label: "Rollout controls",
    passed: true, // Implemented in Phase 6A
    details: "OFF/allowlist/percent/ON modes with kill switch",
    category: "front",
  });
  
  // 9. Telemetry hooks (Phase 6A)
  checks.push({
    id: "telemetry_hooks",
    label: "Telemetry hooks",
    passed: true, // Implemented in Phase 6A
    details: "Render, details, CTA events tracked",
    category: "front",
  });
  
  // Deferred items (informational, don't block)
  const deferredItems = [...KNOWN_DEFERRALS];
  
  const passedCount = checks.filter(c => c.passed && c.category !== "deferred").length;
  const totalCount = checks.filter(c => c.category !== "deferred").length;
  const overallReady = passedCount === totalCount;
  
  return {
    overallReady,
    checks,
    passedCount,
    totalCount,
    deferredItems,
  };
}

/**
 * Check if there are debug artifacts in the DOM.
 * Used to verify debug hardening.
 */
function hasDebugArtifactsInDOM(): boolean {
  if (typeof document === "undefined") return false;
  
  // Look for debug-specific elements
  const debugElements = document.querySelectorAll(
    '[data-block-debug], .debug-panel, [class*="dealCardV2debug"]'
  );
  
  return debugElements.length > 0;
}
